'use client';

import { useRouter } from 'next/navigation';
import { type TransitionStartFunction, useContext, useEffect, useRef } from 'react';
import type { BranchId } from '@/features/funnel/config/constants';
import type { FlattenedFunnelConfig } from '@/features/funnel/config/flattened/types';
import { useAnswerDispatch, useIsAnswerHydrated, useSubmittedAnswers } from '../../answer/hooks';
import { abandonFunnelRun } from '../../answer/session/abandonFunnelRun';
import { canReachStep } from '../branch';
import { ENTRY_STEP_ID } from '../constants';
import { clearPendingBranchSwitch, getPendingBranchSwitch } from '../pendingBranchSwitch';
import { resolveLandingAction } from '../resolveLandingAction';
import type { FunnelStepSnapshot } from '../stepSnapshot';
import { FunnelContext } from '.';

function useFunnelContext() {
  const ctx = useContext(FunnelContext);
  if (!ctx) {
    throw new Error('Funnel context hooks must be used within FunnelProvider');
  }
  return ctx;
}

export function useFunnelFlattenedConfig() {
  return useFunnelContext().funnelFlattenedConfig;
}

export function useFunnelStepId() {
  return useFunnelContext().stepId;
}

export function useBranchId() {
  return useFunnelContext().branchId;
}

export function useFunnelConfigVersion() {
  return useFunnelContext().funnelConfigVersion;
}

export function useIsBranchFirstStep(): boolean {
  return useFunnelContext().isBranchFirstStep;
}

export function useIsBranchLastStep(): boolean {
  return useFunnelContext().isBranchLastStep;
}

export function useIsFirstStep(): boolean {
  return useFunnelContext().isFirstStep;
}

export function useIsFunnelSubmitting(): boolean {
  return useFunnelContext().isSubmitting;
}

export function useStartSubmitTransition(): TransitionStartFunction {
  return useFunnelContext().startSubmitTransition;
}

export function useStepProgress(): number {
  return useFunnelContext().progress;
}

// Validates rendered step is reachable based on the stored answers
// If not, user gets redirected to the entry step
// Also corrects the branch according to the path param
export function useHandleLanding({
  branchId,
  funnelFlattenedConfig,
  stepSnapshot,
}: {
  branchId: BranchId | null;
  funnelFlattenedConfig: FlattenedFunnelConfig;
  stepSnapshot: FunnelStepSnapshot;
}) {
  const { stepId, isFirstStep, isBranchFirstStep } = stepSnapshot;
  const isHydrated = useIsAnswerHydrated();
  const submittedAnswers = useSubmittedAnswers();
  const dispatch = useAnswerDispatch();
  const router = useRouter();
  // Prevent reset + replace from re-firing while still on an unreachable deep URL
  // (session clear + reload used to spam soft-navigations to FINDER_ENTRY_PATH).
  const hasRedirectedRef = useRef(false);

  useEffect(() => {
    const branchFromAnswers = submittedAnswers[ENTRY_STEP_ID];

    // Intentional switch completed: URL branch matches session again.
    if (branchId && typeof branchFromAnswers === 'string' && branchId === branchFromAnswers) {
      clearPendingBranchSwitch();
    }

    const action = resolveLandingAction({
      branchId,
      isHydrated,
      isFirstStep,
      isBranchFirstStep,
      branchFromAnswers,
      pendingBranchSwitch: getPendingBranchSwitch(),
      canReachCurrentStep:
        branchId !== null &&
        canReachStep(funnelFlattenedConfig, branchId, stepId, submittedAnswers),
      hasRedirected: hasRedirectedRef.current,
    });

    switch (action.kind) {
      case 'none':
        return;
      case 'reset-branch':
        dispatch({ type: 'RESET_BRANCH', branchId: action.branchId });
        return;
      case 'clear-redirect-guard':
        hasRedirectedRef.current = false;
        return;
      case 'redirect-to-entry':
        hasRedirectedRef.current = true;
        clearPendingBranchSwitch();
        abandonFunnelRun(dispatch, router);
        return;
    }
  }, [
    branchId,
    isHydrated,
    isFirstStep,
    isBranchFirstStep,
    submittedAnswers,
    funnelFlattenedConfig,
    stepId,
    dispatch,
    router,
  ]);
}
